import React, { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { useGSAP } from "@gsap/react"
import "./cuy-office.scss"

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin, useGSAP)

// Capítulos del recorrido por la oficina (estilo Hubtown, mundo CuySoft)
const ZONES = [
    {
        id: "lobby",
        img: "assets/img/cuy-office/lobby.webp",
        chapter: "Bienvenida",
        tag: "Recepción · CuySoft HQ",
        titleLines: ["Construimos", "el futuro", "del software"],
        text: "Somos una empresa ecuatoriana de innovación tecnológica. Bienvenido a nuestra oficina: recórrela y conoce lo que hacemos.",
        cta: { to: "/services", label: "Explora nuestros servicios" },
    },
    {
        id: "coworking",
        img: "assets/img/cuy-office/coworking.webp",
        chapter: "Desarrollo",
        tag: "Grid-Sector 7 · Coworking",
        titleLines: ["Código que", "convierte ideas", "en productos"],
        text: "Aplicaciones web, móviles y sistemas a medida. Nuestro equipo desarrolla con las tecnologías que mueven al mundo.",
        cta: { to: "/software-development", label: "Ver desarrollo" },
    },
    {
        id: "ciberseguridad",
        img: "assets/img/cuy-office/ciberseguridad.webp",
        chapter: "Seguridad",
        tag: "Cyber-Den · Ciberseguridad",
        titleLines: ["Protegemos", "lo que más", "te importa"],
        text: "Auditorías, pentesting y monitoreo continuo. Tu infraestructura y tus datos, blindados las 24 horas.",
        cta: { to: "/cyber-security", label: "Ver ciberseguridad" },
    },
    {
        id: "warroom",
        img: "assets/img/cuy-office/warroom.webp",
        chapter: "Estrategia",
        tag: "War Room · Proyectos",
        titleLines: ["Planificamos", "cada proyecto", "al detalle"],
        text: "De la pizarra al deploy: diseñamos la estrategia digital de tu negocio con metodologías ágiles.",
        cta: { to: "/services", label: "Conoce nuestros proyectos" },
    },
    {
        id: "cafeteria",
        img: "assets/img/cuy-office/cafeteria.webp",
        chapter: "Equipo",
        tag: "Cuy Café · Nosotros",
        titleLines: ["Las mejores", "ideas nacen", "con un café"],
        text: "Un equipo apasionado por la tecnología y la educación digital en Ecuador. Así somos por dentro.",
        cta: { to: "/about", label: "Conócenos" },
    },
    {
        id: "contacto",
        img: "assets/img/cuy-office/contacto.webp",
        chapter: "Contacto",
        tag: "Hablemos · Soporte",
        titleLines: ["Hablemos", "de tu próximo", "proyecto"],
        text: "Cuéntanos tu idea y la convertimos en realidad. Agenda una reunión con nosotros.",
        cta: { to: "/contact", label: "Contáctanos" },
    },
]

// Videos de transición (el cuy camina de una zona a la siguiente).
// El scroll controla el tiempo del video (scrubbing).
const TRANSITIONS = [
    "assets/video/cuy-office/trans-1.mp4",
    "assets/video/cuy-office/trans-2.mp4",
    "assets/video/cuy-office/trans-3.mp4",
    "assets/video/cuy-office/trans-4.mp4",
    "assets/video/cuy-office/trans-5.mp4",
]

const CuyOfficeExperience = () => {
    const rootRef = useRef(null)
    const stageRef = useRef(null)
    const progressRef = useRef(null)
    const counterRef = useRef(null)
    const stRef = useRef(null)

    const [loadProgress, setLoadProgress] = useState(0)
    const [ready, setReady] = useState(false)
    const [activeZone, setActiveZone] = useState(0)

    // ---------- Preloader: precarga real de las 6 escenas ----------
    useEffect(() => {
        let loaded = 0
        let cancelled = false
        document.body.classList.add("co-lock-scroll")

        ZONES.forEach((zone) => {
            const img = new Image()
            const done = () => {
                if (cancelled) return
                loaded += 1
                setLoadProgress(Math.round((loaded / ZONES.length) * 100))
                if (loaded === ZONES.length) {
                    setTimeout(() => {
                        if (cancelled) return
                        setReady(true)
                        document.body.classList.remove("co-lock-scroll")
                        ScrollTrigger.refresh()
                    }, 700)
                }
            }
            img.onload = done
            img.onerror = done
            img.src = zone.img
        })

        return () => {
            cancelled = true
            document.body.classList.remove("co-lock-scroll")
        }
    }, [])

    // ---------- Mecánica de scroll ----------
    useGSAP(
        () => {
            const mm = gsap.matchMedia()

            mm.add("(min-width: 992px)", () => {
                const zones = gsap.utils.toArray(".co-zone")
                const overlays = gsap.utils.toArray(".co-overlay")
                const chapters = gsap.utils.toArray(".co-chapter-word")
                const videos = gsap.utils.toArray(".co-trans-video")
                const n = ZONES.length
                const SEG = 100

                // Estado inicial: solo la primera zona visible
                zones.forEach((z, i) => gsap.set(z, { autoAlpha: i === 0 ? 1 : 0 }))
                videos.forEach((v) => gsap.set(v, { autoAlpha: 0 }))
                overlays.forEach((o, i) => gsap.set(o, { autoAlpha: i === 0 ? 1 : 0, y: i === 0 ? 0 : 40 }))
                chapters.forEach((c, i) => gsap.set(c, { autoAlpha: i === 0 ? 1 : 0, yPercent: i === 0 ? 0 : 60 }))

                const tl = gsap.timeline({
                    scrollTrigger: {
                        trigger: rootRef.current,
                        start: "top top",
                        end: () => `+=${(n - 1) * window.innerHeight * 1.6}`,
                        pin: stageRef.current,
                        scrub: 0.6,
                        invalidateOnRefresh: true,
                        anticipatePin: 1,
                        onUpdate: (self) => {
                            gsap.set(progressRef.current, { scaleX: self.progress })
                            const idx = Math.min(n - 1, Math.round(self.progress * (n - 1)))
                            setActiveZone(idx)
                            if (counterRef.current) {
                                counterRef.current.textContent = String(idx + 1).padStart(2, "0")
                            }
                        },
                    },
                })

                stRef.current = tl.scrollTrigger

                for (let i = 0; i < n - 1; i++) {
                    const t = i * SEG
                    const video = videos[i]
                    const proxy = { p: 0 }

                    // salida del contenido del capítulo actual
                    tl.to(overlays[i], { autoAlpha: 0, y: -50, duration: SEG * 0.14, ease: "power1.in" }, t + SEG * 0.3)
                    tl.to(chapters[i], { autoAlpha: 0, yPercent: -60, duration: SEG * 0.14, ease: "power1.in" }, t + SEG * 0.3)

                    // el video aparece cubriendo la escena
                    tl.to(video, { autoAlpha: 1, duration: SEG * 0.04 }, t + SEG * 0.42)

                    // swap de zonas mientras el video cubre todo
                    tl.set(zones[i], { autoAlpha: 0 }, t + SEG * 0.6)
                    tl.set(zones[i + 1], { autoAlpha: 1 }, t + SEG * 0.6)

                    // scrubbing: el scroll controla el tiempo del video
                    tl.to(
                        proxy,
                        {
                            p: 1,
                            duration: SEG * 0.5,
                            ease: "none",
                            onUpdate: () => {
                                if (video.readyState >= 1 && video.duration) {
                                    video.currentTime = Math.min(
                                        video.duration - 0.03,
                                        proxy.p * video.duration
                                    )
                                }
                            },
                        },
                        t + SEG * 0.44
                    )

                    // el video se desvanece revelando la escena siguiente
                    tl.to(video, { autoAlpha: 0, duration: SEG * 0.05 }, t + SEG * 0.94)

                    // entrada del contenido del capítulo siguiente
                    tl.fromTo(
                        overlays[i + 1],
                        { autoAlpha: 0, y: 60 },
                        { autoAlpha: 1, y: 0, duration: SEG * 0.18, ease: "power2.out" },
                        t + SEG * 0.86
                    )
                    tl.fromTo(
                        chapters[i + 1],
                        { autoAlpha: 0, yPercent: 60 },
                        { autoAlpha: 1, yPercent: 0, duration: SEG * 0.18, ease: "power2.out" },
                        t + SEG * 0.86
                    )
                }
                // tramo final de reposo en el último capítulo
                tl.to({}, { duration: SEG * 0.5 }, (n - 1) * SEG + SEG * 0.5)
            })

            mm.add("(max-width: 991px)", () => {
                gsap.utils.toArray(".co-overlay").forEach((card) => {
                    gsap.fromTo(
                        card,
                        { autoAlpha: 0, y: 40 },
                        {
                            autoAlpha: 1,
                            y: 0,
                            duration: 0.6,
                            scrollTrigger: { trigger: card, start: "top 82%", toggleActions: "play none none reverse" },
                        }
                    )
                })
            })
        },
        { scope: rootRef, dependencies: [] }
    )

    // ---------- Navegación Prev / Next ----------
    const goTo = (index) => {
        const st = stRef.current
        if (!st) return
        const i = Math.max(0, Math.min(ZONES.length - 1, index))
        const target = st.start + (st.end - st.start) * (i / (ZONES.length - 1))
        gsap.to(window, { scrollTo: target, duration: 1.4, ease: "power2.inOut" })
    }

    return (
        <div className={`cuy-office ${ready ? "is-ready" : ""}`} ref={rootRef}>
            {/* ---------- Preloader ---------- */}
            <div className="co-preloader" aria-hidden={ready}>
                <div className="co-preloader__inner">
                    <span className="co-preloader__percent">{loadProgress}%</span>
                    <span className="co-preloader__label">
                        {loadProgress < 100 ? "Cargando la oficina" : "Listo para explorar"}
                    </span>
                    <div className="co-preloader__bar">
                        <div className="co-preloader__bar-fill" style={{ transform: `scaleX(${loadProgress / 100})` }} />
                    </div>
                </div>
            </div>

            {/* ---------- Escenario pinneado ---------- */}
            <div className="co-stage" ref={stageRef}>
                {ZONES.map((zone, i) => (
                    <div className={`co-zone co-zone--${zone.id}`} key={zone.id}>
                        <div className="co-scene">
                            <img src={zone.img} alt={`Oficina CuySoft – ${zone.tag}`} loading={i === 0 ? "eager" : "lazy"} />
                            <div className="co-scene__shade" />
                        </div>
                        <div className="co-overlay">
                            <span className="co-overlay__tag">{zone.tag}</span>
                            <h2 className="co-overlay__title">
                                {zone.titleLines.map((line) => (
                                    <span key={line}>{line}</span>
                                ))}
                            </h2>
                            <p className="co-overlay__text">{zone.text}</p>
                            <Link className="co-overlay__cta" to={zone.cta.to}>
                                {zone.cta.label} →
                            </Link>
                        </div>
                    </div>
                ))}

                {/* Videos de transición: el cuy camina entre oficinas, controlado por el scroll */}
                {TRANSITIONS.map((src, i) => (
                    <video
                        className="co-trans-video"
                        key={src}
                        src={src}
                        muted
                        playsInline
                        preload={i === 0 ? "auto" : "metadata"}
                        disablePictureInPicture
                    />
                ))}

                {/* Palabras-capítulo gigantes */}
                <div className="co-chapters" aria-hidden="true">
                    {ZONES.map((zone) => (
                        <span className="co-chapter-word" key={zone.id}>
                            {zone.chapter}
                        </span>
                    ))}
                </div>

                {/* UI de navegación */}
                <div className="co-ui">
                    <div className="co-counter">
                        <span ref={counterRef}>01</span>
                        <em>/ {String(ZONES.length).padStart(2, "0")}</em>
                    </div>
                    <div className="co-nav">
                        <button type="button" aria-label="Capítulo anterior" onClick={() => goTo(activeZone - 1)} disabled={activeZone === 0}>
                            ←
                        </button>
                        <button type="button" aria-label="Siguiente capítulo" onClick={() => goTo(activeZone + 1)} disabled={activeZone === ZONES.length - 1}>
                            →
                        </button>
                    </div>
                    <div className="co-hint">Scrollea para recorrer la oficina</div>
                </div>

                <div className="co-progress">
                    <div className="co-progress__bar" ref={progressRef} />
                </div>

                {/* Acción rápida flotante */}
                <Link to="/contact" className="co-float-cta" aria-label="Hablemos">
                    💬
                </Link>
            </div>
        </div>
    )
}

export default CuyOfficeExperience
